import { CompositeScreenProps } from "@react-navigation/native"
import { observer } from "mobx-react-lite"
import { View } from "native-base"
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Appearance, Dimensions, ImageStyle, TextStyle, ViewStyle } from "react-native"
import { AutoImage, Button, GrayscaleImage, Screen, Text } from "../../../components"
import { useStores } from "../../../models"
import { AppStackInitialSync, resetRoot } from "../../../navigators"
import { color } from "../../../theme"

const windowWidth = Dimensions.get('window').width;

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const PADDING: TextStyle = {
  paddingHorizontal: 16
}

const BTN_PADDING: ViewStyle = {
  paddingHorizontal: 16,
  paddingVertical: 16,
  flex: 1,
  justifyContent: 'flex-end',
}

const AREA: ImageStyle = {
  width: windowWidth - 32,
  height: 104 * (windowWidth - 32) / 328
}

const BTN: ViewStyle = {
  borderRadius: 10,
  paddingVertical: 18
}


const interestImage = {
  en: {
    management: require("./en_interest_farming.png"),
    price: require("./en_interest_price.png"),
    study: require("./en_interest_learning.png"),
    information: require("./en_interest_info.png")
  },
  id: {
    management: require("./interest_farming.png"),
    price: require("./interest_price.png"),
    study: require("./interest_learning.png"),
    information: require("./interest_info.png")
  },
  vi: {
    management: require("./vi_interest_farming.png"),
    price: require("./vi_interest_price.png"),
    study: require("./vi_interest_learning.png"),
    information: require("./vi_interest_info.png")
  },
  es: {
    management: require("./en_interest_farming.png"),
    price: require("./en_interest_price.png"),
    study: require("./en_interest_learning.png"),
    information: require("./en_interest_info.png")
  },
};

export const InterestScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function InterestScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route } = props

  // Pull in one of our MST stores
  const { appStore: { locale }, userStore, userStore: { user: { interests }, loading } } = useStores();

  const [isActive, setActive] = useState<Array<any>>([]);

  const type = useMemo<any>(() => {
    const isLight = route.name === "Profile.Change.Interest"
    return !!isLight
  },[route.name])

  const initInterest = useCallback(() => {
    if (interests) {
      setActive(interests);
    }
  },[interests])

  useEffect(() => {
    initInterest();
  },[initInterest])

  const handleSelect = useCallback((label: string) => () => {
    const filtered = isActive.filter((i) => i !== label);
    const activeYet = isActive.find((item) => item === label);
    if (activeYet) setActive(filtered);
    else setActive([...isActive, label]);
  }, [isActive])

  const handleUpdate = useCallback(() => {
    const body = { interests: isActive };
    userStore.updateInterest(body).then(() => resetRoot(AppStackInitialSync))
  }, [isActive, userStore])

  const ImageButton = useCallback(({ label, accessibilityLabel }: { label: string, accessibilityLabel?: string }) => {
    const imageSource = interestImage[locale];
    const source = imageSource[label] || "";
    const active = isActive.find((i) => i === label);
    return (
      <Button testID="jala:id/item-button" accessibilityLabel={accessibilityLabel} preset="link" onPress={handleSelect(label)} colorScheme={colorScheme} style={PADDING} >
        {active ? <AutoImage source={source} style={AREA} resizeMode="contain" /> : <GrayscaleImage source={source} style={AREA} resizeMode="contain" />}
      </Button>
    )
  }, [colorScheme, handleSelect, isActive, locale]);

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={type ? color.primary : color.palette.white}
      statusBar={type ? "light-content" : "dark-content"}
      safeAreaEdges={['top', 'bottom']}
    >
      <Text preset="display2" tx="personalisationScreen.PersonalizationTitle" mt="16px" colorScheme={colorScheme} style={PADDING} />
      <Text preset="body2" tx="personalisationScreen.PersonalizationSubtitle" mt="8px" mb="20px" colorScheme={colorScheme} style={PADDING} />
      <ImageButton label="management" accessibilityLabel="managing_card" />
      <ImageButton label="price" accessibilityLabel="shrimp_card" />
      <ImageButton label="information" accessibilityLabel="news_card" />
      <ImageButton label="study" accessibilityLabel="learning_card" />
      <View style={BTN_PADDING}>
        <Button
          testID="jala:id/save-button"
          textPreset="title3"
          tx="personalisationScreen.button"
          accessibilityLabel="next_page"
          colorScheme={colorScheme}
          disabled={isActive.length === 0}
          onPress={handleUpdate} loading={loading}
          style={BTN}
        />
      </View>
    </Screen>
  )
})
